import { Tooltip } from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import PauseCircleOutlineIcon from '@material-ui/icons/PauseCircleOutline';
import { makeStyles, useTheme } from '@material-ui/styles';
import React, { useContext } from 'react';
import LocalizationContext from '../../contexts/LocalizationContext';
import SubmitResult from '../../enums/SubmitResults';
import IFormSubmitResult from '../../interfaces/IFormSubmitResult';

const useStyles = makeStyles({
    statusIcon: {
        float: 'left',
    },
    statusText: {
        display: 'inline-block',
        lineHeight: '24px',
        marginLeft: '6px',
        fontSize: '14px',
        fontWeight: 600,
    },
    statusContainer: {
        display: 'flex',
        margin: '0 -12px',
    },
    errorMessages: {
        fontFamily: 'Consolas',
        fontSize: '13px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        padding: '10px 15px',
        overflowWrap: 'anywhere',
    },
});

interface FormSubmitResultProps {
    result: IFormSubmitResult | undefined;
}

const FormSubmitResult = ({ result }: FormSubmitResultProps) => {
    const localization = useContext(LocalizationContext);
    const classes = useStyles();

    return (
        <>
            {/* Unknow error */}
            {!result || (result.errors.length === 0 && result.genericException) ? (
                <>{localization.strings.tryAgain}</>
            ) : (
                <>
                    <div style={{ marginBottom: '5px' }}>{localization.strings.reviewMessages}</div>
                    {!result.genericException && (
                        <div className={classes.statusContainer}>
                            <OperationStatus status={result.pdf} text={localization.strings.pdf} />
                            <OperationStatus status={result.email} text={localization.strings.email} />
                            <OperationStatus status={result.mFiles} text={localization.strings.mFiles} />
                            <OperationStatus status={result.database} text={localization.strings.database} />
                            <OperationStatus status={result.storage} text={localization.strings.fileStorage} />
                        </div>
                    )}
                    <div className={classes.errorMessages}>{result.errors.map((e) => e.summary).join('\r\n')}</div>
                </>
            )}
        </>
    );
};

interface OperationStatusProps {
    status: SubmitResult;
    text: string;
}

const OperationStatus = ({ status, text }: OperationStatusProps) => {
    const localization = useContext(LocalizationContext);
    const classes = useStyles();
    const theme: any = useTheme<any>();

    if (status === SubmitResult.NotApplicable) return <></>;

    function getSymbol(status: SubmitResult): JSX.Element | undefined {
        switch (status) {
            case SubmitResult.Successful:
                return (
                    <Tooltip title={localization.strings.success}>
                        <CheckCircleOutlineIcon className={classes.statusIcon} style={{ color: theme.palette.success.main }} />
                    </Tooltip>
                );
            case SubmitResult.PartiallySuccessful:
                return (
                    <Tooltip title={localization.strings.partialSuccess}>
                        <CheckCircleOutlineIcon className={classes.statusIcon} style={{ color: theme.palette.warning.main }} />
                    </Tooltip>
                );
            case SubmitResult.Failed:
                return (
                    <Tooltip title={localization.strings.failed}>
                        <ErrorOutlineIcon className={classes.statusIcon} style={{ color: theme.palette.error.main }} />
                    </Tooltip>
                );
            case SubmitResult.DidNotRun:
                return (
                    <Tooltip title={localization.strings.didNotRun}>
                        <PauseCircleOutlineIcon className={classes.statusIcon} />
                    </Tooltip>
                );
        }
    }

    return (
        <div style={{ margin: '12px' }}>
            {getSymbol(status)}
            <div className={classes.statusText}>{text}</div>
        </div>
    );
};

export default FormSubmitResult;
